import { useEffect, useState } from 'react';
import { useDispatch, useProject } from '@witmetrics/data-store';
import {
  buildProjectTypeOption,
  buildUserOption,
  getProjectType,
} from '@witmetrics/unison-core';
import type { DecoratedLead, UnisonProjectTypeId } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import { showSuccessToaster, showWarningToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { searchLeads } from '@/utils/options';
import { useOption } from '@/hooks/useOption';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import NewLeadDialog from '@/dialogs/NewLeadDialog';
import FormBlock from '@/components/FormBlock';
import TextField from '@/components/inputs/TextField';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from '@/components/inputs/SearchableDropdown';
import SelectDropdown from '@/components/inputs/SelectDropdown';
import { PROJECT_TYPE_OPTIONS } from './constants';
import { classes, updateProject, validateFields } from './utils';

export type ProjectDialogProps = {
  projectId: number;
  onClose: () => void;
};

export default function ProjectDialog({
  projectId,
  onClose,
}: ProjectDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const project = useProject(projectId);
  const [projectType, setProjectType] = useOption();
  const [name, setName] = useString();
  const [externalRef, setExternalRef] = useString();
  const [description, setDescription] = useString();
  const [initialFirstname, setInitialFirstname] = useString();
  const [lead, setLead] = useState<SearchableDropdownOption<number> | null>(
    null
  );
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isLeadDialogOpen, toggleLeadDialog] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (project) fillFields();
  }, [project]);

  const fillFields = () => {
    setName(project!.name);
    setExternalRef(project!.externalRef ?? '');
    setDescription(project!.description);
    const projectTypeOption = getProjectType(project!.unisonProjectTypeId);
    if (projectTypeOption) {
      setProjectType(buildProjectTypeOption(projectTypeOption));
    }
    if (project?.user) setLead(buildUserOption(project.user));
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const userId = lead?.value;
      const invalid = validateFields({ name, projectType, userId });
      if (!isEmpty(invalid)) {
        showWarningToaster('Please complete all required fields');
        toggleSaving(false);
        return setInvalidFields(invalid);
      }
      const data = {
        name,
        externalRef,
        description,
        userId,
        unisonProjectTypeId: projectType?.value as UnisonProjectTypeId,
      };
      // Have the project name default to the lead's name if it's empty
      if (lead && isEmpty(name)) data.name = lead.label;
      const updatedProject = await updateProject(projectId, data);
      dispatch.addProjects([updatedProject]);
      showSuccessToaster('Case updated');
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving case', () => handleSave());
    }
  };

  const handleStartNewLead = (search: string) => {
    setInitialFirstname(search);
    toggleLeadDialog(true);
  };

  const handleSelectNewLead = (lead: DecoratedLead) => {
    setLead(buildUserOption(lead));
    toggleLeadDialog(false);
  };

  const isSaveDisabled = () => {
    return (
      isSaving ||
      validateFields({ name, projectType, userId: lead?.value }).length > 0
    );
  };

  return (
    <>
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle title="Edit Case" onClose={onClose} />
        <DialogBody className={classes.body}>
          <FormBlock
            title="Name"
            subtitle="Select a customer and/or provide a name for the case">
            <div className={classes.section}>
              <SearchableDropdown<number>
                allowNew
                autoFocus
                className={classes.dropdown}
                label="Select a Customer"
                value={lead}
                initialOptions={lead ? [lead] : []}
                onSearch={searchLeads}
                onAddNew={handleStartNewLead}
                onSelect={setLead}
              />
              <TextField
                error={!isValidField('name')}
                className={classes.textField}
                label="Name"
                value={name}
                onChange={setName}
              />
              <TextField
                className={classes.textField}
                label="External Ref."
                value={externalRef}
                onChange={setExternalRef}
              />
            </div>
          </FormBlock>
          <FormBlock
            title="Details"
            subtitle="Provide additional information about the project (optional)">
            <div className={classes.section}>
              <SelectDropdown
                className={classes.dropdown}
                label="Case type"
                value={projectType}
                options={PROJECT_TYPE_OPTIONS}
                onChange={setProjectType}
              />
              <TextField
                multiline
                className={classes.textField}
                minRows={3}
                maxRows={5}
                label="Description"
                value={description}
                onChange={setDescription}
              />
            </div>
          </FormBlock>
        </DialogBody>
        <DefaultDialogActions
          disabled={isSaveDisabled()}
          pending={isSaving}
          confirmLabel="Save"
          pendingLabel="Saving"
          onCancel={onClose}
          onConfirm={handleSave}
        />
      </Dialog>
      {isLeadDialogOpen && (
        <NewLeadDialog
          initialFirstname={initialFirstname}
          onSelect={handleSelectNewLead}
          onClose={() => toggleLeadDialog(false)}
        />
      )}
    </>
  );
}
